import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { SearchService } from '@nucleus/ui-base';
import { UserFilter } from './user-filter';
import { ServiceHandler } from '@nucleus/ui-base';
import { PageResult } from '@nucleus/ui-base';
import { Page, QuerySortDirection } from '@nucleus/ui-base';
import { FilterRequest } from '../filter-request';
import { ThemeService } from '@nucleus/ui-common';
import {
  AccessRequestPayload as RequestAccessPayload,
  BlockUserPayload,
  ChangePasswordPayload,
  PaginatedFilterRequestUserFilter,
  ResetPasswordPayload,
  CreateUserPayload as CreateUserPayload,
  UpdateUserPayload as UpdateUserPayload,
  UnblockUserPayload,
  UpdateMyProfile as UpdateMyProfilePayload,
  User,
  UserFilter as ApiUserFilter,
  UserControllerService,
} from '@nucleus/user-ui';
import { Oauth2ControllerService } from '../../api/authentication-service';
import { RequestHandler } from '../request-handler';


@Injectable({
  providedIn: 'root'
})
export class UserService implements SearchService<User, UserFilter> {

  private themeService: ThemeService = inject(ThemeService);
  private currentUsername: string | null = null;

  /** Cache for /user/me so APP_INITIALIZER's load (initialize.function.ts)
   *  and HeaderComponent.ngOnInit don't both hit the backend. Populated on
   *  first successful fetch; cleared by {@link invalidateMyProfileCache} when
   *  the profile changes (theme, after-save behaviour, etc.). */
  private cachedMyProfile?: User;

  constructor(
    private userApi: UserControllerService,
    private oauth2Api: Oauth2ControllerService,
    private requestHandler: RequestHandler,
  ) {
    this.themeService.registerBackendPersist((theme: string) => this.persistTheme(theme));
  }

  private persistTheme(theme: string): void {
    if (!this.currentUsername) {
      return;
    }
    this.invalidateMyProfileCache();
    this.requestHandler.handleRequest(this.userApi.updateMyTheme(theme), {
      onStart: () => {},
      onSuccess: () => {},
      onError: (err: string) => console.warn(`[ThemePersist] failed to save theme=${theme}: ${err}`),
      onComplete: () => {}
    }, false);
  }

  search(handler: ServiceHandler<User[]>): void {
    this.requestHandler.handleRequest(this.userApi.findAll() as unknown as Observable<User[]>, handler, false);
  }

  searchPaginated(handler: ServiceHandler<PageResult<User>>, pageSize: number, pageNumber: number, softField: string, sortDirection: QuerySortDirection) {
  }

  filterPaginated(handler: ServiceHandler<PageResult<User>>, pageSize: number, pageNumber: number, softField: string, sortDirection: QuerySortDirection, filter: UserFilter): void {
    let request = new FilterRequest<UserFilter>(filter);
    request.page.pageSize = pageSize;
    request.page.pageNumber = pageNumber;
    request.page.sortDirection = sortDirection;
    request.page.sortField = softField;

    this.filter(handler, request.filter, request.page);
  }


  filter(handler: ServiceHandler<PageResult<User>>, filter: UserFilter, page?: Page, _clientId?: string): void {
    const request = new FilterRequest<UserFilter>(filter);
    if (page) { request.page = page; }
    const apiRequest = this.toApiFilterRequest(request);
    this.requestHandler.handleRequest(
      this.userApi.filter(apiRequest) as unknown as Observable<PageResult<User>>,
      handler,
      false,
    );
  }

  /** UI's UserFilter now carries the canonical status enum directly
   *  (TASK-141 retired the boolean translation hack). Pass-through. */
  private toApiFilterRequest(request: FilterRequest<UserFilter>): PaginatedFilterRequestUserFilter {
    const f = request.filter;
    return {
      page: {
        pageSize: request.page?.pageSize,
        pageNumber: request.page?.pageNumber,
        sortField: request.page?.sortField,
        sortDirection: request.page?.sortDirection as unknown as string,
      },
      filter: {
        value: f.value,
        permissions: f.permissions,
        status: f.status as unknown as ApiUserFilter.StatusEnum,
        includeDeleted: f.includeDeleted,
      },
    };
  }


  block(payload: BlockUserPayload, handler: ServiceHandler<User>): void {
    this.requestHandler.handleRequest(this.userApi.block(payload) as unknown as Observable<User>, handler, false);
  }

  unblock(payload: UnblockUserPayload, handler: ServiceHandler<User>): void {
    this.requestHandler.handleRequest(this.userApi.unblock(payload) as unknown as Observable<User>, handler, false);
  }

  /** NUC-79 Phase E: soft-delete the user. Backend sets users.deleted_at;
   *  next login is rejected, listings hide the row. */
  softDelete(username: string, handler: ServiceHandler<void>) {
    this.requestHandler.handleRequest(this.userApi.softDelete(username), handler, true);
  }

  /** NUC-79 Phase E: clear deleted_at so the user can log in / appear in listings again. */
  restore(username: string, handler: ServiceHandler<void>) {
    this.requestHandler.handleRequest(this.userApi.restore(username), handler, true);
  }

  logout(handler: ServiceHandler<void>) {
    this.requestHandler.handleRequest(this.oauth2Api.logout(), handler, false);
  }

  /** Pulls userId + authorities from authentication-service /oauth2/me. The
   *  Angular layer can't decode the JWT (HttpOnly cookie under the BFF flow),
   *  so the auth-service is the source of truth for the current profile. */
  loadProfile(handler: ServiceHandler<any>) {
    this.requestHandler.handleRequest(this.oauth2Api.me(), handler, true);
  }


  create(payload: CreateUserPayload, handler: ServiceHandler<User>) {
    this.requestHandler.handleRequest(
      this.userApi.create(payload) as unknown as Observable<User>,
      handler,
      true,
    );
  }


  update(payload: UpdateUserPayload, handler: ServiceHandler<User>) {
    this.requestHandler.handleRequest(
      this.userApi.update(payload.userId!, payload) as unknown as Observable<User>,
      handler,
      true,
    );
  }

  requestAccess(payload: RequestAccessPayload, handler: ServiceHandler<void>) {
    this.requestHandler.handleRequest(this.userApi.requestAccess(payload), handler, true);
  }

  findMyPendingAccessRequests(handler: ServiceHandler<string[]>) {
    this.requestHandler.handleRequest(this.userApi.myPendingAccessRequests(), handler, true);
  }

  resetPassword(payload: ResetPasswordPayload, handler: ServiceHandler<User>) {
    this.requestHandler.handleRequest(this.userApi.resetPassword(payload) as unknown as Observable<User>, handler, true);
  }


  changePassword(payload: ChangePasswordPayload, handler: ServiceHandler<User>) {
    this.invalidateMyProfileCache();
    this.requestHandler.handleRequest(this.userApi.changePassword(payload) as unknown as Observable<User>, handler, true);
  }

  findByUsername(username: string, handler: ServiceHandler<User>) {
    this.requestHandler.handleRequest(this.userApi.findByUsername(username) as unknown as Observable<User>, handler, false);
  }


  getPDFReport(handler: ServiceHandler<Blob>, request: FilterRequest<UserFilter>) {
    this.requestHandler.handleRequest(this.userApi.createPdfReport(this.toApiFilterRequest(request)), handler, true);
  }


  getXLSReport(handler: ServiceHandler<Blob>, request: FilterRequest<UserFilter>) {
    this.requestHandler.handleRequest(this.userApi.createExcelReport(this.toApiFilterRequest(request)), handler, true);
  }


  getCSVReport(handler: ServiceHandler<Blob>, request: FilterRequest<UserFilter>) {
    this.requestHandler.handleRequest(this.userApi.createCSVReport(this.toApiFilterRequest(request)), handler, true);
  }


  getMyProfile(handler: ServiceHandler<User>): void {
    // Cache hit — fire the handler synchronously. APP_INITIALIZER warms the
    // cache from initialize.function.ts; HeaderComponent.ngOnInit then gets
    // it for free instead of re-hitting /user/me.
    if (this.cachedMyProfile) {
      handler.onStart();
      handler.onSuccess(this.cachedMyProfile);
      handler.onComplete();
      return;
    }
    const wrapped: ServiceHandler<User> = {
      ...handler,
      onSuccess: (user: User) => {
        this.cachedMyProfile = user;
        this.currentUsername = user.username;
        this.themeService.hydrateFromBackend(user.userPreferences?.theme);
        handler.onSuccess(user);
      },
    };
    this.requestHandler.handleRequest(this.userApi.findMyProfile() as unknown as Observable<User>, wrapped, false);
  }

  /** Drop the cached /user/me payload. Call this after any backend mutation
   *  that changes the current user's record (theme save, password change,
   *  etc.) so the next {@link getMyProfile} hits the backend. */
  invalidateMyProfileCache(): void {
    this.cachedMyProfile = undefined;
  }

  updateMyProfile(payload: UpdateMyProfilePayload, handler: ServiceHandler<User>) {
    this.invalidateMyProfileCache();
    this.requestHandler.handleRequest(this.userApi.updateMyProfile(payload) as unknown as Observable<User>, handler, true);
  }
}
